import React, { useEffect, useRef, useState } from 'react';

interface FockeLogoAnimatedProps {
  animate?: boolean; // Optional, defaults to animating on mount
}

const FockeLogoAnimated: React.FC<FockeLogoAnimatedProps> = ({ animate }) => {
  // Animate on mount if animate is undefined
  const [mounted, setMounted] = useState(false);
  const pathsRef = useRef<SVGPathElement[]>([]);
  const polygonsRef = useRef<SVGPolygonElement[]>([]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const doAnimate = animate === undefined ? mounted : animate;
    
    // Add a small delay to ensure SVG is fully rendered, especially on mobile
    const animationTimeout = setTimeout(() => {
      // Animate the F (first polygon) first, then the rest
      const all = [...pathsRef.current, ...polygonsRef.current];
      // F is polygonsRef.current[0]
      const rest = all.filter(el => el !== polygonsRef.current[0]);
      const sequence = [polygonsRef.current[0], ...rest];
      
      sequence.forEach((el, i) => {
        if (!el) return;
        
        try {
          // Check if element is connected to DOM and rendered
          if (!el.isConnected) {
            console.warn('SVG element not connected to DOM');
            return;
          }
          
          // Check if element is rendered and has a valid bounding box
          const bbox = el.getBoundingClientRect();
          if (bbox.width === 0 && bbox.height === 0) {
            console.warn('SVG element not rendered, skipping animation');
            return;
          }
          
          const length = el.getTotalLength();
          el.style.transition = 'none';
          el.style.strokeDasharray = String(length);
          el.style.strokeDashoffset = String(length);
          el.style.stroke = 'white';
          el.style.fill = 'transparent';
          el.style.opacity = '1';
          el.style.transform = 'scale(1)';
          void el.getBoundingClientRect();
          
          // Animate F first, then the rest
          setTimeout(() => {
            el.style.transition =
              'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1), fill 0.7s 0.7s cubic-bezier(0.4,0,0.2,1)';
            el.style.strokeDashoffset = doAnimate ? '0' : String(length);
            setTimeout(() => {
              el.style.fill = doAnimate ? 'white' : 'transparent';
            }, 800);
          }, i === 0 ? 0 : 800 + (i - 1) * 200);
        } catch (error) {
          console.warn('Error animating SVG element:', error);
          // Fallback: just show the element without animation
          el.style.stroke = 'white';
          el.style.fill = 'white';
          el.style.opacity = '1';
        }
      });
    }, 100); // 100ms delay to ensure rendering
    
    return () => clearTimeout(animationTimeout);
  }, [animate, mounted]);

  return (
    <svg
      viewBox="1.8 0 840 74.4"
      className="h-9 w-auto ml-6 p-2 rounded-lg bg-transparent"
      style={{ display: 'block' }}
    >
      <polygon
        ref={el => { if (el) polygonsRef.current[0] = el; }}
        points="1.8,0.8 77,0.8 77,18.2 23.8,18.2 23.8,31.7 74.4,31.7 74.4,48.6 23.8,48.6 23.8,73.4 1.8,73.4 "
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <path
        ref={el => { if (el) pathsRef.current[0] = el; }}
        d="M88.8,26.4C88.8,5.1,100.5,0,120.5,0H156c19.9,0,31.7,5.1,31.7,26.4v21.7c0,21-11.8,26.1-31.7,26.1h-35.6  c-20,0-31.7-5.1-31.7-26.4L88.8,26.4z M111.5,40.9c0,10.5,2.3,14.6,12.5,14.6h28.4c10.5,0,12.5-4.1,12.5-14.6v-7.7  c0-9-2.3-14.6-13.8-14.6h-25.8c-11.8,0-13.8,5.4-13.8,14.6V40.9z"
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <polygon
        ref={el => { if (el) polygonsRef.current[1] = el; }}
        points="303.6,0.8 325.9,0.8 325.9,27.4 336.1,27.4 364,0.8 394.2,0.8 355,36.3 398.8,73.4 367.5,73.4  335.6,45.5 325.9,45.5 325.9,73.4 303.6,73.4 "
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <polygon
        ref={el => { if (el) polygonsRef.current[2] = el; }}
        points="406.9,0.8 485.7,0.8 485.7,17.6 429.2,17.6 429.2,29.2 482.9,29.2 482.9,44.3 429.2,44.3 429.2,55.8  487,55.8 487,73.4 406.9,73.4 "
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <path
        ref={el => { if (el) pathsRef.current[1] = el; }}
        d="M743.1,26.4c0-21.2,12-26.4,31.7-26.4h35.3c20,0,31.7,5.1,31.7,26.4v21.7c0,21.2-11.8,26.3-31.7,26.3h-35.3  c-20,0-31.7-5.1-31.7-26.4L743.1,26.4L743.1,26.4z M765.8,40.9c0,10.5,2.3,14.6,12.5,14.6h28.5c10.5,0,12.5-4.1,12.5-14.6v-7.7  c0-9-2.3-14.6-13.8-14.6h-25.9c-11.8,0-13.8,5.4-13.8,14.6L765.8,40.9z"
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <path
        ref={el => { if (el) pathsRef.current[2] = el; }}
        d="M234.8,54.7c-10.5,0-12.5-3.3-12.5-13.8V34c0-9,2.3-14.6,13.8-14.6H288V0.8h-56.8c-20,0-31.7,5.1-31.7,26.4v20  c0,21.2,11.8,26.3,31.7,26.3h57.6V54.7H234.8z"
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <path
        ref={el => { if (el) pathsRef.current[3] = el; }}
        d="M675,54.7c-10.5,0-12.5-3.3-12.5-13.8V34c0-9,2.3-14.6,13.8-14.6h51.9V0.8h-56.8c-20,0-31.7,5.1-31.7,26.4v20  c0,21.2,11.8,26.3,31.7,26.3H729V54.7H675z"
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
      <path
        ref={el => { if (el) pathsRef.current[4] = el; }}
        d="M606.7,56V39.9h-17.9v6.7l-38.4-20c-1.3-0.4-2.1-1.7-2-3.1c0-2.5,2-3.3,7.7-3.3h32.7V5.6h-35  c-22.5,0-26.1,2.8-26.1,16.6c0,8.7,0.5,11,7.4,15.3c-12.3,0.3-15.3,4.6-15.6,15.9c0,16.4,10.5,20.5,26.9,20.5H576  c9.5,0,19.7-0.5,23-5.1l10,5.1l6.9-13L606.7,56z M578.8,58.6h-29.7c-6.4,0-9.5-1.5-9.5-8.2c0-3.6,1.8-6.6,4.6-6.6h2.1L578.8,58.6z"
        stroke="white"
        strokeWidth={3}
        fill="transparent"
      />
    </svg>
  );
};

export default FockeLogoAnimated;
